<script setup lang="ts">
import { computed } from "vue";
import DateSelector from "@/components/DateSelector.vue";
import OrderSummary from "@/components/OrderSummary.vue";
import TicketCard from "@/components/TicketCard.vue";
import TicketsEmptyState from "@/components/TicketsEmptyState.vue";
import TicketsListLoadingState from "@/components/TicketsListLoadingState.vue";
import type { TicketDateOption, TicketOrderLine, TicketType } from "@/types";

const props = defineProps<{
  dates: TicketDateOption[];
  calendarDates: TicketDateOption[];
  selectedDateId: string;
  tickets: TicketType[];
  quantities: Record<string, number>;
  lines: TicketOrderLine[];
  totalPrice: number;
  formatPrice: (value: number) => string;
  isTicketsLoading: boolean;
}>();

const emit = defineEmits<{
  selectDate: [dateId: string];
  quantityChange: [ticket: TicketType, quantity: number];
  checkout: [];
}>();

const selectedDateLabel = computed(
  () =>
    props.calendarDates.find((date) => date.id === props.selectedDateId)
      ?.label ?? "",
);
</script>

<template>
  <section
    class="grid w-full items-start gap-6 xl:grid-cols-[minmax(0,1fr)_500px] xl:gap-5 [&>*]:min-w-0"
  >
    <div class="grid min-w-0 gap-6 lg:gap-10">
      <DateSelector
        :dates="dates"
        :modal-dates="calendarDates"
        :selected-date-id="selectedDateId"
        :format-price="formatPrice"
        @select="emit('selectDate', $event)"
      />

      <div>
        <p
          class="mb-3 lg:mb-4 gap-2 flex items-center text-sm lg:text-lg font-normal text-ink/80"
        >
          Доступные тарифы на:
          <span class="text-brand font-semibold">{{ selectedDateLabel }}</span>
        </p>

        <div class="grid gap-3 lg:gap-4">
          <TicketsListLoadingState v-if="isTicketsLoading" />
          <TicketsEmptyState v-else-if="!tickets.length" />
          <template v-else>
            <TicketCard
              v-for="ticket in tickets"
              :key="ticket.id"
              :ticket="ticket"
              :quantity="quantities[ticket.id] ?? 0"
              :format-price="formatPrice"
              @quantity-change="
                (changedTicket, quantity) =>
                  emit('quantityChange', changedTicket, quantity)
              "
            />
          </template>
        </div>
      </div>
    </div>

    <OrderSummary
      class="hidden xl:sticky xl:top-8 xl:block"
      :lines="lines"
      :total-price="totalPrice"
      :format-price="formatPrice"
      action-label="Купить билет"
      :is-disabled="!lines.length"
      @action="emit('checkout')"
    />
  </section>
</template>
